import React, { useState, useEffect } from 'react';
import { Trophy, Crown, Medal, ChevronDown, Gamepad2, User, Calendar, Share2  } from 'lucide-react';
import { gamesData } from '../static/GamesConst';
import { useAuth } from '../context/AuthContext';
import { ShareScoreModal } from '../components/ShareScoreModal';
import API_URL from '../config/APIURL';
export function Leaderboard() {
  const [selectedGame, setSelectedGame] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('all-time');
  const [leaderboards, setLeaderboards] = useState({});
  const [loading, setLoading] = useState(true);
  const [showGameDropdown, setShowGameDropdown] = useState(false);
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);

  const [showShareModal, setShowShareModal] = useState(false);
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    fetchLeaderboards();
  }, [selectedPeriod]);

  const fetchLeaderboards = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/games/leaderboards?period=${selectedPeriod}`);
      const data = await response.json();
      setLeaderboards(data.leaderboards || {});
    } catch (error) {
      console.error('Failed to fetch leaderboards:', error);
    }
    setLoading(false);
  };

  const getGameInfo = (gameId) => {
    const gameIdMap = {
      'cug-jump': 2,
      'brick-builder': 1,
      'neon-hades': 3,
      'neon-shadow': 4,
      'react-survivors': 5,
      'cqb-school': 6,
      'multiplier': 7,
      'paper-flight': 8
    };
    const id = gameIdMap[gameId];
    return gamesData.find(g => g.id === id) || { title: gameId, thumbnail: '' };
  };

  const getPeriodLabel = (period) => {
    const labels = {
      'all-time': 'All Time',
      'monthly': 'This Month',
      'weekly': 'This Week'
    };
    return labels[period] || period;
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown size={20} color="#FFD700" />;
    if (rank === 2) return <Medal size={20} color="#C0C0C0" />;
    if (rank === 3) return <Medal size={20} color="#CD7F32" />;
    return <span style={{ 
      fontFamily: "'Orbitron', monospace", 
      fontSize: '0.9rem',
      color: 'rgba(255,255,255,0.5)',
      width: '20px',
      textAlign: 'center'
    }}>{rank}</span>;
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return { background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.2), rgba(255, 215, 0, 0.05))', border: '1px solid rgba(255, 215, 0, 0.5)' };
    if (rank === 2) return { background: 'linear-gradient(135deg, rgba(192, 192, 192, 0.15), rgba(192, 192, 192, 0.05))', border: '1px solid rgba(192, 192, 192, 0.4)' };
    if (rank === 3) return { background: 'linear-gradient(135deg, rgba(205, 127, 50, 0.15), rgba(205, 127, 50, 0.05))', border: '1px solid rgba(205, 127, 50, 0.4)' };
    return { background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.1)' };
  };

  const gameIds = Object.keys(leaderboards);
  const displayGames = selectedGame === 'all' ? gameIds : [selectedGame];

  return (
    <section style={{
      padding: '60px 40px',
      background: 'linear-gradient(180deg, rgba(0, 245, 255, 0.02) 0%, rgba(255, 0, 255, 0.02) 100%)'
    }}>
      {/* Section Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '40px',
        flexWrap: 'wrap',
        gap: '20px'
      }}>
        <h2 style={{
          fontFamily: "'Orbitron', monospace",
          fontSize: '2rem',
          fontWeight: '700',
          display: 'flex',
          alignItems: 'center',
          gap: '15px'
        }}>
          <span style={{
            width: '5px',
            height: '40px',
            background: 'linear-gradient(180deg, #FFD700, #ff00ff)',
            borderRadius: '3px',
            display: 'inline-block'
          }} />
          <Trophy size={32} color="#FFD700" />
          LEADERBOARDS
        </h2>

        {/* Filter Controls */}
        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
          {/* Time Period Filter */}
          <button
            onClick={() => isAuthenticated ? setShowShareModal(true) : alert('Login to share your scores!')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              background: 'linear-gradient(135deg, rgba(0, 245, 255, 0.1), rgba(255, 0, 255, 0.1))',
              border: '1px solid rgba(0, 245, 255, 0.3)',
              borderRadius: '8px',
              color: '#00f5ff',
              fontFamily: "'Orbitron', monospace",
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            <Share2 size={18} />
            Share Score
          </button>
          <div style={{ position: 'relative' }}>
            
            <button
              onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 20px',
                background: 'rgba(255, 0, 255, 0.1)',
                border: '1px solid rgba(255, 0, 255, 0.3)',
                borderRadius: '10px',
                color: '#ff00ff',
                fontFamily: "'Orbitron', monospace",
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                minWidth: '180px',
                justifyContent: 'space-between'
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Calendar size={18} />
                {getPeriodLabel(selectedPeriod)}
              </span>
              <ChevronDown size={18} style={{ 
                transform: showPeriodDropdown ? 'rotate(180deg)' : 'rotate(0)',
                transition: 'transform 0.3s ease'
              }} />
            </button>

            {showPeriodDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                background: 'rgba(10, 10, 15, 0.98)',
                border: '1px solid rgba(255, 0, 255, 0.3)',
                borderRadius: '10px',
                padding: '8px',
                minWidth: '180px',
                zIndex: 50,
                backdropFilter: 'blur(10px)'
              }}>
                {['all-time', 'monthly', 'weekly'].map(period => (
                  <button
                    key={period}
                    onClick={() => { setSelectedPeriod(period); setShowPeriodDropdown(false); }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 15px',
                      background: selectedPeriod === period ? 'rgba(255, 0, 255, 0.1)' : 'transparent',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#fff',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontFamily: "'Rajdhani', sans-serif",
                      fontSize: '1rem',
                      fontWeight: '500'
                    }}
                  >
                    {getPeriodLabel(period)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Game Filter */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowGameDropdown(!showGameDropdown)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 20px',
                background: 'rgba(0, 245, 255, 0.1)',
                border: '1px solid rgba(0, 245, 255, 0.3)',
                borderRadius: '10px',
                color: '#00f5ff',
                fontFamily: "'Orbitron', monospace",
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                minWidth: '200px',
                justifyContent: 'space-between'
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Gamepad2 size={18} />
                {selectedGame === 'all' ? 'All Games' : getGameInfo(selectedGame).title}
              </span>
              <ChevronDown size={18} style={{ 
                transform: showGameDropdown ? 'rotate(180deg)' : 'rotate(0)',
                transition: 'transform 0.3s ease'
              }} />
            </button>

            {showGameDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                background: 'rgba(10, 10, 15, 0.98)',
                border: '1px solid rgba(0, 245, 255, 0.3)',
                borderRadius: '10px',
                padding: '8px',
                minWidth: '220px',
                zIndex: 50,
                backdropFilter: 'blur(10px)'
              }}>
                <button
                  onClick={() => { setSelectedGame('all'); setShowGameDropdown(false); }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '10px 15px',
                    background: selectedGame === 'all' ? 'rgba(0, 245, 255, 0.1)' : 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fff',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: "'Rajdhani', sans-serif",
                    fontSize: '1rem',
                    fontWeight: '500'
                  }}
                >
                  All Games
                </button>
                {gameIds.map(gameId => (
                  <button
                    key={gameId}
                    onClick={() => { setSelectedGame(gameId); setShowGameDropdown(false); }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px 15px',
                      background: selectedGame === gameId ? 'rgba(0, 245, 255, 0.1)' : 'transparent',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#fff',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontFamily: "'Rajdhani', sans-serif",
                      fontSize: '1rem',
                      fontWeight: '500'
                    }}
                  >
                    {getGameInfo(gameId).title}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '3px solid rgba(0, 245, 255, 0.2)',
            borderTop: '3px solid #00f5ff',
            borderRadius: '50%',
            margin: '0 auto',
            animation: 'spin 1s linear infinite'
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ marginTop: '20px', color: 'rgba(255,255,255,0.5)' }}>Loading leaderboards...</p>
        </div>
      )}

      {/* Leaderboards Grid - Rest remains the same */}
      {!loading && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: selectedGame === 'all' ? 'repeat(auto-fill, minmax(350px, 1fr))' : '1fr',
          gap: '30px',
          maxWidth: selectedGame === 'all' ? '100%' : '600px',
          margin: selectedGame === 'all' ? '0' : '0 auto'
        }}>
          {displayGames.map(gameId => {
            const gameInfo = getGameInfo(gameId);
            const scores = leaderboards[gameId] || [];

            return (
              <div
                key={gameId}
                style={{
                  background: 'rgba(10, 10, 20, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  backdropFilter: 'blur(10px)'
                }}
              >
                {/* Game Header */}
                <div style={{
                  padding: '20px',
                  background: 'linear-gradient(135deg, rgba(0, 245, 255, 0.1), rgba(255, 0, 255, 0.1))',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px'
                }}>
                  <img
                    src={gameInfo.thumbnail}
                    alt={gameInfo.title}
                    style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '10px',
                      objectFit: 'cover',
                      border: '2px solid rgba(0, 245, 255, 0.3)'
                    }}
                  />
                  <div>
                    <h3 style={{
                      fontFamily: "'Orbitron', monospace",
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      color: '#fff',
                      margin: 0
                    }}>
                      {gameInfo.title}
                    </h3>
                    <p style={{
                      fontSize: '0.8rem',
                      color: 'rgba(255, 255, 255, 0.5)',
                      margin: '4px 0 0 0'
                    }}>
                      Top {scores.length} Players
                    </p>
                  </div>
                </div>

                {/* Scores List */}
                <div style={{ padding: '15px' }}>
                  {scores.length === 0 ? (
                    <p style={{
                      textAlign: 'center',
                      color: 'rgba(255, 255, 255, 0.4)',
                      padding: '30px',
                      fontSize: '0.9rem'
                    }}>
                      No scores yet. Be the first!
                    </p>
                  ) : (
                    scores.map((entry, index) => (
                      <div
                        key={entry.id || index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '12px 15px',
                          marginBottom: '8px',
                          borderRadius: '10px',
                          transition: 'all 0.3s ease',
                          ...getRankStyle(index + 1)
                        }}
                      >
                        <div style={{ width: '30px', display: 'flex', justifyContent: 'center' }}>
                          {getRankIcon(index + 1)}
                        </div>

                        <div style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #00f5ff, #ff00ff)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <User size={16} color="#000" />
                          </div>
                          <span style={{
                            fontFamily: "'Rajdhani', sans-serif",
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#fff'
                          }}>
                            {entry.username || `Player ${entry.user_id}`}
                          </span>
                        </div>

                        <div style={{
                          fontFamily: "'Orbitron', monospace",
                          fontSize: '1.2rem',
                          fontWeight: '700',
                          color: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : '#00f5ff'
                        }}>
                          {entry.score.toLocaleString()}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!loading && gameIds.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '80px 40px',
          background: 'rgba(255, 255, 255, 0.02)',
          borderRadius: '20px',
          border: '1px dashed rgba(255, 255, 255, 0.1)'
        }}>
          <Trophy size={64} color="rgba(255, 255, 255, 0.2)" style={{ marginBottom: '20px' }} />
          <h3 style={{
            fontFamily: "'Orbitron', monospace",
            fontSize: '1.5rem',
            color: 'rgba(255, 255, 255, 0.5)',
            marginBottom: '10px'
          }}>
            No Leaderboards Yet
          </h3>
          <p style={{ color: 'rgba(255, 255, 255, 0.3)' }}>
            Play some games and be the first on the leaderboard!
          </p>
        </div>
      )}
      <ShareScoreModal 
        show={showShareModal} 
        onClose={() => setShowShareModal(false)}
        userId={user?.id}
      />

    </section>
  );
}